import React from 'react';
import type { Automation } from '../data/automationLibrary';

interface AutomationCardProps {
  automation: Automation;
  onClick: () => void;
}

const AutomationCard: React.FC<AutomationCardProps> = ({ automation, onClick }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#22c55e';
      case 'paused': return '#f59e0b';
      case 'development': return '#3b82f6';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getBrandEmoji = (brand: string) => {
    switch (brand) {
      case 'mars-men':
        return '🚀';
      case 'hollow-socks':
        return '🧦';
      case 'easy-street':
        return '🛣️';
      default:
        return '⚙️';
    }
  };

  const formatBrandLabel = (brand: string) => {
    switch (brand) {
      case 'mars-men':
        return 'Mars Men';
      case 'hollow-socks':
        return 'Hollow Socks';
      case 'easy-street':
        return 'Easy Street';
      default:
        return brand.replace('-', ' ');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Review Management': return '⭐';
      case 'Content Generation': return '✍️';
      case 'Inventory Management': return '📦';
      case 'Marketing': return '📣';
      case 'Customer Support': return '💬';
      default: return '⚙️';
    }
  };

  const formatLastRun = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  };

  return (
    <div className="automation-card" onClick={onClick}>
      <div className="automation-card-header">
        <div className="automation-icon">{getCategoryIcon(automation.category)}</div>
        <div className="automation-status">
          <span 
            className="status-dot"
            style={{ backgroundColor: getStatusColor(automation.status) }}
          />
          <span className="status-text">{automation.status}</span>
        </div>
      </div>
      
      <h3 className="automation-name">{automation.name}</h3>
      <p className="automation-description">{automation.description}</p>
      
      <div className="automation-meta">
        <div className="meta-item">
          <span className="meta-label">Category</span>
          <span className="meta-value">{automation.category}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Last Run</span>
          <span className="meta-value">{formatLastRun(automation.lastRun)}</span>
        </div>
      </div>

      {automation.metrics && (
        <div className="automation-stats">
          <div className="stat">
            <span className="stat-value">{automation.metrics.runsToday}</span>
            <span className="stat-label">Today</span>
          </div>
          <div className="stat">
            <span className="stat-value">{automation.metrics.successRate}%</span>
            <span className="stat-label">Success</span>
          </div>
          <div className="stat">
            <span className="stat-value">{automation.metrics.avgExecutionTime}</span>
            <span className="stat-label">Avg Time</span>
          </div>
        </div>
      )}

      <div className="automation-brands">
        {automation.brands
          .filter((brand) => brand === 'all' || brand === 'easy-street')
          .map((brand) => {
            const normalized = brand.replace(/[^a-z0-9]/gi, '-');
            return (
              <span key={brand} className={`brand-chip brand-${normalized}`}>
                {getBrandEmoji(brand)} {formatBrandLabel(brand)}
              </span>
            );
          })}
      </div>
    </div>
  );
};

export default AutomationCard;
